/* Yummy Gummy — PDP "Loved by thousands" video testimonials.
   Native <video>, no controls: a click toggles play/pause. While paused the
   card shows its caption + a play icon; while playing both hide (the pause
   icon appears on hover only). Only one video plays at a time — starting one
   pauses the others. Safe & idempotent; guarded so a missing piece no-ops. */
(function () {
  'use strict';

  function initList(list) {
    if (!list || list.__pdpTvBound) return;
    list.__pdpTvBound = true;

    var cards = Array.prototype.slice.call(list.querySelectorAll('[data-pdp-tv-card]'));

    function pauseOthers(except) {
      cards.forEach(function (card) {
        if (card === except) return;
        var v = card.querySelector('[data-pdp-tv-video]');
        if (v && !v.paused) v.pause();
      });
    }

    cards.forEach(function (card) {
      var video = card.querySelector('[data-pdp-tv-video]');
      var toggle = card.querySelector('[data-pdp-tv-toggle]');
      if (!video) return;

      function play() {
        pauseOthers(card);
        // The video starts muted (so it's autoplay/loop-safe), but a click is
        // a real user gesture, so we can un-mute it here for sound on play.
        video.muted = false;
        if (video.volume === 0) video.volume = 1;
        // Show an immediate "loading" state on the card so the click feels
        // responsive while the first frames buffer (videos preload metadata
        // only, so the media itself starts fetching on this first play). The
        // class is cleared as soon as playback actually begins or enough data
        // is buffered.
        card.classList.add('is-loading');
        // Nudge the browser to start fetching now rather than lazily.
        if (video.preload !== 'auto') video.preload = 'auto';
        if (video.readyState < 2) { try { video.load(); } catch (e) {} }
        var p = video.play();
        // Some browsers reject play() (e.g. not yet loaded) — swallow it so a
        // failed attempt doesn't throw; the class only flips on real playback.
        if (p && typeof p.catch === 'function') p.catch(function () { card.classList.remove('is-loading'); });
      }

      // Clear the loading state once the video can actually play through.
      video.addEventListener('playing', function () { card.classList.remove('is-loading'); });
      video.addEventListener('canplay', function () { card.classList.remove('is-loading'); });

      function toggleState() {
        if (video.paused) play();
        else video.pause();
      }

      // Reflect real playback state onto the card (drives icon + caption CSS)
      // so it stays correct even if playback ends or is paused elsewhere.
      video.addEventListener('play', function () {
        card.classList.add('is-playing');
        if (toggle) toggle.setAttribute('aria-label', 'Pause video');
      });
      video.addEventListener('pause', function () {
        card.classList.remove('is-playing');
        if (toggle) toggle.setAttribute('aria-label', 'Play video');
      });
      video.addEventListener('ended', function () {
        card.classList.remove('is-playing');
      });

      if (toggle) {
        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          toggleState();
        });
      }
    });
  }

  /* Scroll rail: size the thumb to the visible fraction of the row and slide it
     with scrollLeft, so the hairline under the videos doubles as a position
     indicator. Purely presentational — the row stays scrollable/swipeable on
     its own if this never runs. When the list doesn't overflow (desktop, three
     fixed columns) the thumb spans the full rail and reads as a plain divider. */
  function initRail(inner) {
    if (!inner || inner.__pdpTvRailBound) return;
    var list = inner.querySelector('[data-pdp-tv-list]');
    var thumb = inner.querySelector('[data-pdp-tv-thumb]');
    if (!list || !thumb) return;
    inner.__pdpTvRailBound = true;

    var rail = inner.querySelector('[data-pdp-tv-rail]');

    function maxScroll() {
      return Math.max(0, list.scrollWidth - list.clientWidth);
    }

    function update() {
      var visible = list.clientWidth;
      var total = list.scrollWidth;
      if (!total || !visible) return;
      var ratio = Math.min(1, visible / total);
      // Cap the thumb short of the full rail whenever the row genuinely
      // scrolls, so some grey track always remains visible ahead of it — a
      // thumb spanning the whole rail reads as "nothing to scroll".
      if (total - visible > 1) ratio = Math.min(ratio, 0.92);
      thumb.style.setProperty('--pdp-tv-thumb-width', (ratio * 100).toFixed(3) + '%');

      // Travel is measured in rail pixels: the thumb may move across the part
      // of the rail it doesn't itself cover.
      var scrollable = maxScroll();
      var railWidth = rail ? rail.clientWidth : visible;
      var travel = railWidth - railWidth * ratio;
      var offset = scrollable > 0 ? (list.scrollLeft / scrollable) * travel : 0;
      thumb.style.setProperty('--pdp-tv-thumb-offset', offset.toFixed(2) + 'px');
    }

    // Clicking a card's caption advances the row by one card, so the strip can
    // be moved without arrows (reference behaviour). Stepping by the card's own
    // width plus the gap lands on the next card's snap point.
    function step(dir) {
      var item = list.querySelector('.pdp-tv__item');
      var by = item ? item.getBoundingClientRect().width + 12 : list.clientWidth * 0.4;
      list.scrollBy({ left: dir * by, behavior: 'smooth' });
    }
    list.querySelectorAll('[data-pdp-tv-caption]').forEach(function (cap) {
      if (cap.__pdpTvCapBound) return;
      cap.__pdpTvCapBound = true;
      cap.addEventListener('click', function (e) {
        // The caption sits over the full-surface play/pause button, so stop the
        // click here — a caption click scrolls, it doesn't start the video.
        e.preventDefault();
        e.stopPropagation();
        // At the end of the row, wrap back to the start rather than dead-ending.
        if (list.scrollLeft >= maxScroll() - 1) list.scrollTo({ left: 0, behavior: 'smooth' });
        else step(1);
      });
    });

    // Rail drag/click — map a pointer position on the rail to a scroll
    // position. Uses pointer capture so a drag keeps tracking outside the rail.
    if (rail) {
      var dragging = false;

      function scrollToPointer(clientX) {
        var box = rail.getBoundingClientRect();
        if (!box.width) return;
        var ratio = Math.min(1, list.clientWidth / list.scrollWidth);
        var travel = box.width - box.width * ratio;
        if (travel <= 0) return;
        // Aim the CENTRE of the thumb at the pointer, so the grab point doesn't
        // jump to the thumb's left edge on press.
        var pos = clientX - box.left - (box.width * ratio) / 2;
        var clamped = Math.max(0, Math.min(travel, pos));
        list.scrollLeft = (clamped / travel) * maxScroll();
      }

      rail.addEventListener('pointerdown', function (e) {
        if (maxScroll() <= 1) return;
        dragging = true;
        rail.classList.add('is-dragging');
        list.classList.add('is-rail-dragging');
        if (rail.setPointerCapture) { try { rail.setPointerCapture(e.pointerId); } catch (err) {} }
        scrollToPointer(e.clientX);
        e.preventDefault();
      });
      rail.addEventListener('pointermove', function (e) {
        if (dragging) scrollToPointer(e.clientX);
      });
      function endDrag() {
        dragging = false;
        rail.classList.remove('is-dragging');
        list.classList.remove('is-rail-dragging');
      }
      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);
    }

    list.addEventListener('scroll', update, { passive: true });
    if (typeof ResizeObserver === 'function') {
      // Observe the ITEMS, not just the list: the list's own width never
      // changes, so watching only it would miss the moment the cards resolve
      // their real widths (their aspect-ratio boxes settle as layout lands).
      // Until that happens scrollWidth equals clientWidth, which reads as
      // "nothing to scroll" and leaves the thumb stuck at full width.
      var ro = new ResizeObserver(update);
      ro.observe(list);
      list.querySelectorAll('.pdp-tv__item').forEach(function (item) { ro.observe(item); });
    } else {
      window.addEventListener('resize', update);
    }
    update();
    // Posters and video metadata can land after first layout and change the
    // row's scrollWidth, so recheck once everything has loaded too.
    window.addEventListener('load', update);
  }

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-pdp-tv-list]').forEach(initList);
    scope.querySelectorAll('.pdp-tv__inner').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
